import { useTranslation } from 'react-i18next'
import { FileText, Send, CheckCircle, XCircle, RotateCcw, Clock, Activity } from 'lucide-react'
import { useActivity } from '../../hooks/useActivity'
import type { ActivityEntry } from '../../hooks/useActivity'

interface ActivityLogProps {
  koblinkId: string
  actorId?: string
  enabled?: boolean
}

const actionIcons: Record<string, typeof Activity> = {
  created: FileText,
  uploaded: FileText,
  sent: Send,
  signed: CheckCircle,
  rejected: XCircle,
  recalled: RotateCcw,
  expired: Clock,
  completed: CheckCircle,
}

/** Actions considered "completed" for dot styling */
const completedActions = new Set(['created', 'uploaded', 'sent', 'signed', 'completed'])

function formatTimestamp(iso: string): string {
  const date = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffHours = diffMs / (1000 * 60 * 60)

  if (diffHours < 1) {
    const diffMin = Math.floor(diffMs / (1000 * 60))
    return diffMin <= 1 ? '1 min ago' : `${diffMin} min ago`
  }
  if (diffHours < 24) {
    return `${Math.floor(diffHours)}h ago`
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function formatFullTimestamp(iso: string): string {
  const date = new Date(iso)
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ', ' +
    date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

export function ActivityLog({ koblinkId, actorId, enabled = true }: ActivityLogProps) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useActivity(koblinkId, actorId, enabled)

  if (isLoading) {
    return (
      <div className="text-[11px] text-neutral-400 py-2 text-center">
        {t('common.loading')}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-[11px] text-neutral-400 py-2 text-center">
        {t('followup.activityUnavailable')}
      </div>
    )
  }

  if (!data?.activities?.length) {
    return (
      <div className="text-[11px] text-neutral-400 py-2 text-center">
        {t('followup.noActivity')}
      </div>
    )
  }

  return (
    <div className="relative py-1">
      {/* Vertical timeline line */}
      <div className="absolute left-[7px] top-3 bottom-3 w-[2px] bg-neutral-200 dark:bg-neutral-700" />

      {data.activities.map((entry, index) => (
        <TimelineItem
          key={entry.id}
          entry={entry}
          isLast={index === data.activities.length - 1}
        />
      ))}
    </div>
  )
}

function TimelineItem({ entry, isLast }: { entry: ActivityEntry; isLast: boolean }) {
  const Icon = actionIcons[entry.action] || Activity
  const isCompleted = completedActions.has(entry.action)
  const isSubEvent = entry.category === 'system'

  return (
    <div className={`relative flex items-start gap-2.5 ${isLast ? '' : 'pb-3'} ${isSubEvent ? 'ml-4' : ''}`}>
      {/* Timeline dot */}
      <div
        className={`relative z-10 flex items-center justify-center shrink-0 rounded-full ${
          isSubEvent
            ? 'w-3 h-3 mt-1 bg-neutral-100 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600'
            : isCompleted
              ? 'w-4 h-4 mt-0.5 bg-primary-500 dark:bg-primary-600'
              : 'w-4 h-4 mt-0.5 bg-white dark:bg-neutral-900 border-2 border-neutral-300 dark:border-neutral-600'
        }`}
      >
        {!isSubEvent && isCompleted && (
          <Icon size={9} className="text-white" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <p className={`leading-tight truncate ${
            isSubEvent
              ? 'text-[10px] text-neutral-500 dark:text-neutral-400'
              : 'text-[11px] text-neutral-700 dark:text-neutral-300'
          }`}>
            {entry.description || entry.action_display}
          </p>
          {entry.performed_by_name && !entry.performed_by_system && (
            <span className="text-[10px] text-neutral-400 dark:text-neutral-500 truncate block">
              {entry.performed_by_name}
            </span>
          )}
        </div>
        <span className="text-[10px] text-neutral-400 shrink-0" title={formatFullTimestamp(entry.created_at)}>
          {formatTimestamp(entry.created_at)}
        </span>
      </div>
    </div>
  )
}
